'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import AccordionSection from '@/app/[locale]/(default-layout)/(forms)/_components/AccordionSection';
import { getFormContact } from '@/app/[locale]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/(default-layout)/(forms)/_components/FormContactLinks';
import KidsNTeensForm from '@/app/[locale]/(default-layout)/(forms)/_components/KidsNTeensForm';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type KidsNTeensPageClientProps = {
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const KidsNTeensPageClient: React.FC<KidsNTeensPageClientProps> = ({ hallImageUrl }) => {
    const t = useTranslations();
    const isMounted = useIsMounted();
    const domainConfiguration = useDomainConfiguration();

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'kidsNTeens');

    const heroImage = resolveFormImageUrl(domainConfiguration, 'kidsNTeens', hallImageUrl);
    const customDescription = resolveFormPageText(domainConfiguration, 'kidsNTeens');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'kidsNTeens');

    return (
        <main className="min-h-screen">
            {/* Hero Section */}
            <div className="py-[96px] space-y-[48px]">
                <section>
                    <CenterLayout>
                        <div className="text-center space-y-8">
                            <div className="space-y-4">
                                <h1 className="text-headline-32 text-[var(--on-background-color)]">
                                    {customPageTitle || t('kids-n-teens.hero.title')}
                                </h1>
                                {!customDescription && (
                                    <h2 className="text-headline-24 font-semibold text-[var(--primary-color)]">
                                        {t('kids-n-teens.hero.subtitle')}
                                    </h2>
                                )}
                            </div>
                            <div className="text-body-18 max-w-3xl mx-auto text-[rgba(var(--on-background-color-rgb),0.8)]">
                                {customDescription ? (
                                    <CinemaDescriptionMarkdown
                                        description={customDescription}
                                        allowInlineHtml
                                    />
                                ) : (
                                    t('kids-n-teens.hero.description')
                                )}
                            </div>
                            {heroImage && isMounted && (
                                <div className="relative w-full max-w-3xl mx-auto aspect-[16/9] rounded-12 overflow-hidden">
                                    <Image
                                        src={heroImage}
                                        alt={t('kids-n-teens.hero.title')}
                                        fill
                                        className="object-cover"
                                        sizes="(max-width: 768px) 100vw, 768px"
                                    />
                                </div>
                            )}
                        </div>
                    </CenterLayout>
                </section>

                {/* Benefits section is default copy, so a custom page text replaces it too —
                    otherwise the operator's wording would sit above boilerplate they cannot edit. */}
                {!customDescription && (
                    <section>
                        <CenterLayout>
                            <div className="max-w-6xl mx-auto">
                                <h2 className="text-headline-32 text-center mb-16 text-[var(--on-background-color)]">
                                    {t('kids-n-teens.benefits.title')}
                                </h2>
                                <div className="grid md:grid-cols-2 gap-12">
                                    <div className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[var(--surface-color)]">
                                        <div className="size-[48px] mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-[32px] font-bold drop-shadow-sm text-[var(--on-primary-color)]">
                                                €
                                            </span>
                                        </div>
                                        <h3 className="text-headline-20 text-[var(--on-surface-color)]">
                                            {t('kids-n-teens.benefits.free-membership')}
                                        </h3>
                                    </div>
                                    <div className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[var(--surface-color)]">
                                        <div className="size-[48px] mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-[32px] font-bold drop-shadow-sm text-[var(--on-primary-color)]">
                                                €
                                            </span>
                                        </div>
                                        <h3 className="text-headline-20 text-[var(--on-surface-color)]">
                                            {t('kids-n-teens.benefits.reduced-prices')}
                                        </h3>
                                    </div>
                                    <div className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[var(--surface-color)]">
                                        <div className="size-[48px] mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-[32px] font-bold drop-shadow-sm text-[var(--on-primary-color)]">
                                                🍿
                                            </span>
                                        </div>
                                        <h3 className="text-headline-20 text-[var(--on-surface-color)]">
                                            {t('kids-n-teens.benefits.special-menu')}
                                        </h3>
                                    </div>
                                    <div className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[var(--surface-color)]">
                                        <div className="size-[48px] mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-[32px] font-bold drop-shadow-sm text-[var(--on-primary-color)]">
                                                ⭐
                                            </span>
                                        </div>
                                        <h3 className="text-headline-20 text-[var(--on-surface-color)]">
                                            {t('kids-n-teens.benefits.points-system')}
                                        </h3>
                                    </div>
                                    <div className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[var(--surface-color)]">
                                        <div className="size-[48px] mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-[32px] font-bold drop-shadow-sm text-[var(--on-primary-color)]">
                                                📧
                                            </span>
                                        </div>
                                        <h3 className="text-headline-20 text-[var(--on-surface-color)]">
                                            {t('kids-n-teens.benefits.newsletter')}
                                        </h3>
                                    </div>
                                    <div className="text-center space-y-6 px-8 py-24 rounded-16 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[var(--surface-color)]">
                                        <div className="size-[48px] mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-[32px] font-bold drop-shadow-sm text-[var(--on-primary-color)]">
                                                🎫
                                            </span>
                                        </div>
                                        <h3 className="text-headline-20 text-[var(--on-surface-color)]">
                                            {t('kids-n-teens.benefits.welcome-ticket')}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </CenterLayout>
                    </section>
                )}
            </div>

            {/* Extras and age groups are default copy as well, so a custom page text replaces the
                whole editorial part of the page — only the form and the contact block remain. */}
            {!customDescription && (
                <>
                    {/* Extras & Vorteile Section */}
                    <section className="py-32 bg-[rgba(var(--on-background-color-rgb),0.05)]">
                        <CenterLayout>
                            <div className="max-w-4xl mx-auto">
                                <h2 className="text-headline-32 text-center mb-16 text-[var(--on-background-color)]">
                                    {t('kids-n-teens.extras.title')}
                                </h2>
                                <div className="space-y-6">
                                    <AccordionSection
                                        title={t('kids-n-teens.extras.membership.title')}
                                        defaultOpen={true}>
                                        {t('kids-n-teens.extras.membership.description')}
                                    </AccordionSection>
                                    <AccordionSection title={t('kids-n-teens.extras.everyday-cinema.title')}>
                                        {t('kids-n-teens.extras.everyday-cinema.description')}
                                    </AccordionSection>
                                    <AccordionSection title={t('kids-n-teens.extras.junior-menu.title')}>
                                        {t('kids-n-teens.extras.junior-menu.description')}
                                    </AccordionSection>
                                    <AccordionSection title={t('kids-n-teens.extras.points-rewards.title')}>
                                        {t('kids-n-teens.extras.points-rewards.description')}
                                    </AccordionSection>
                                    <AccordionSection title={t('kids-n-teens.extras.exclusive-info.title')}>
                                        {t('kids-n-teens.extras.exclusive-info.description')}
                                    </AccordionSection>
                                    <AccordionSection title={t('kids-n-teens.extras.welcome-gift.title')}>
                                        {t('kids-n-teens.extras.welcome-gift.description')}
                                    </AccordionSection>
                                </div>
                            </div>
                        </CenterLayout>
                    </section>

                    {/* Age Groups Section */}
                    <section className="py-32">
                        <CenterLayout>
                            <div className="max-w-6xl mx-auto">
                                <h2 className="text-headline-32 text-center mb-16 text-[var(--on-background-color)]">
                                    {t('kids-n-teens.age-groups.title')}
                                </h2>
                                <div className="grid md:grid-cols-2 gap-12">
                                    <div className="text-center space-y-6">
                                        <div className="size-24 mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-36 font-bold text-[var(--on-primary-color)]">👶</span>
                                        </div>
                                        <h3 className="text-headline-24 text-[var(--on-background-color)]">
                                            {t('kids-n-teens.age-groups.kids.title')}
                                        </h3>
                                        <p className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                                            {t('kids-n-teens.age-groups.kids.description')}
                                        </p>
                                    </div>
                                    <div className="text-center space-y-6">
                                        <div className="size-24 mx-auto rounded-full flex items-center justify-center bg-[var(--primary-color)]">
                                            <span className="text-36 font-bold text-[var(--on-primary-color)]">🧒</span>
                                        </div>
                                        <h3 className="text-headline-24 text-[var(--on-background-color)]">
                                            {t('kids-n-teens.age-groups.teens.title')}
                                        </h3>
                                        <p className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                                            {t('kids-n-teens.age-groups.teens.description')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CenterLayout>
                    </section>
                </>
            )}

            {/* Registration Form Section */}
            <section className="py-32">
                <CenterLayout>
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-headline-32 mb-4 text-[var(--on-background-color)]">
                                {t('kids-n-teens.form.title')}
                            </h2>
                            <p className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                                {t('kids-n-teens.form.subtitle')}
                            </p>
                        </div>

                        <KidsNTeensForm />
                    </div>
                </CenterLayout>
            </section>

            {/* Contact Section */}
            <section className="py-32 backdrop-blur-sm bg-[rgba(var(--on-background-color-rgb),0.05)]">
                <CenterLayout>
                    <div className="text-center space-y-8">
                        <h2 className="text-headline-32 text-[var(--on-background-color)]">
                            {t('kids-n-teens.contact.question')}
                        </h2>
                        <p className="text-body-16 max-w-2xl mx-auto text-[rgba(var(--on-background-color-rgb),0.8)]">
                            {t('kids-n-teens.contact.help-text')}
                        </p>
                        <FormContactLinks
                            namespace="kids-n-teens"
                            phone={phone}
                            phoneURI={phoneURI}
                            email={email}
                        />
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default KidsNTeensPageClient;
